import { z } from "zod";

// The language other modules use to describe a set of prospects: conditions
// over prospect fields (filters build on them) and scopes (an analysis, a set
// of analyses, a filter's rules, picked rows). Prospect owns the data, so it
// owns the language and evaluates it.

export const conditionFieldSchema = z.enum([
  "biography",
  "username",
  "email",
  "website",
  "phone",
  "country",
  "language",
  "niche",
  "category",
  "address",
  "followers",
  "following",
  "rating",
  "reviews",
  "avgLikes",
  "avgComments",
  "engagementRate",
  "isBusiness",
  "hasEmail",
  "hasPhone",
  "hasWebsite",
  "gender",
  "entityType",
  "emailType",
  "b2bB2c",
  "qualification",
]);
export type ConditionField = z.infer<typeof conditionFieldSchema>;

export const operatorSchema = z.enum([
  "equals",
  "notEquals",
  "contains",
  "notContains",
  "startsWith",
  "endsWith",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "in",
  "notIn",
  "exists",
  "notExists",
  "isTrue",
  "isFalse",
]);
export type Operator = z.infer<typeof operatorSchema>;

export type FieldType = "text" | "number" | "boolean" | "enum";
export type FieldCategory = "profile" | "metrics" | "business" | "contact" | "location";

export type ConditionFieldInfo = {
  readonly id: ConditionField;
  readonly label: string;
  readonly category: FieldCategory;
  readonly type: FieldType;
  readonly options?: ReadonlyArray<{ readonly value: string; readonly label: string }>;
};

export const CONDITION_FIELDS: readonly ConditionFieldInfo[] = [
  { id: "biography", label: "Biography", category: "profile", type: "text" },
  { id: "username", label: "Username", category: "profile", type: "text" },
  { id: "country", label: "Country", category: "profile", type: "text" },
  { id: "language", label: "Language", category: "profile", type: "text" },
  { id: "niche", label: "Niche / Industry", category: "profile", type: "text" },
  {
    id: "gender",
    label: "Gender",
    category: "profile",
    type: "enum",
    options: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "unknown", label: "Unknown" },
    ],
  },
  { id: "followers", label: "Followers", category: "metrics", type: "number" },
  { id: "following", label: "Following", category: "metrics", type: "number" },
  { id: "avgLikes", label: "Avg. Likes", category: "metrics", type: "number" },
  { id: "avgComments", label: "Avg. Comments", category: "metrics", type: "number" },
  { id: "engagementRate", label: "Engagement Rate (%)", category: "metrics", type: "number" },
  { id: "isBusiness", label: "Is business account", category: "business", type: "boolean" },
  {
    id: "entityType",
    label: "Person or Business",
    category: "business",
    type: "enum",
    options: [
      { value: "person", label: "Person" },
      { value: "business", label: "Business" },
    ],
  },
  {
    id: "b2bB2c",
    label: "B2B / B2C",
    category: "business",
    type: "enum",
    options: [
      { value: "b2b", label: "B2B (Business-to-Business)" },
      { value: "b2c", label: "B2C (Business-to-Consumer)" },
      { value: "unknown", label: "Unknown" },
    ],
  },
  { id: "hasEmail", label: "Has email", category: "contact", type: "boolean" },
  { id: "hasPhone", label: "Has phone number", category: "contact", type: "boolean" },
  { id: "hasWebsite", label: "Has website", category: "contact", type: "boolean" },
  { id: "email", label: "Email", category: "contact", type: "text" },
  { id: "phone", label: "Phone number", category: "contact", type: "text" },
  { id: "website", label: "Website", category: "contact", type: "text" },
  {
    id: "emailType",
    label: "Email type",
    category: "contact",
    type: "enum",
    options: [
      { value: "b2b", label: "B2B (Business)" },
      { value: "b2c", label: "B2C (Personal)" },
    ],
  },
  {
    id: "qualification",
    label: "Qualification",
    category: "contact",
    type: "enum",
    options: [
      { value: "interested", label: "Interested" },
      { value: "not-interested", label: "Not interested" },
      { value: "wrong-person", label: "Wrong person" },
      { value: "do-not-contact", label: "Do not contact" },
      { value: "out-of-office", label: "Out of office" },
    ],
  },
  { id: "category", label: "Business type", category: "location", type: "text" },
  { id: "address", label: "Address", category: "location", type: "text" },
  { id: "rating", label: "Rating", category: "location", type: "number" },
  { id: "reviews", label: "Reviews count", category: "location", type: "number" },
];

/** Which operators make sense for each field type. */
export const OPERATORS_BY_TYPE: Readonly<Record<FieldType, readonly Operator[]>> = {
  text: ["contains", "notContains", "equals", "notEquals", "startsWith", "endsWith", "exists", "notExists"],
  number: ["greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual", "equals", "notEquals"],
  boolean: ["isTrue", "isFalse"],
  enum: ["in", "notIn"],
};

export const OPERATOR_LABELS: Readonly<Record<Operator, string>> = {
  equals: "is equal to",
  notEquals: "is not equal to",
  contains: "contains",
  notContains: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
  greaterThan: "is greater than",
  greaterThanOrEqual: "is greater than or equal to",
  lessThan: "is less than",
  lessThanOrEqual: "is less than or equal to",
  in: "is one of",
  notIn: "is not one of",
  exists: "exists",
  notExists: "does not exist",
  isTrue: "is true",
  isFalse: "is false",
};

export const conditionSchema = z.object({
  field: conditionFieldSchema,
  operator: operatorSchema,
  value: z.union([z.string(), z.number(), z.array(z.string()), z.null()]),
});
export type Condition = z.infer<typeof conditionSchema>;

export const MAX_CONDITIONS = 10;

/** A set of prospects: empty means all of the user's prospects. Every part narrows. */
export const prospectScopeSchema = z.object({
  analysisIds: z.array(z.string()).max(10).optional(),
  conditions: z.array(conditionSchema).max(MAX_CONDITIONS).optional(),
  prospectIds: z.array(z.string()).max(5000).optional(),
});
export type ProspectScope = z.infer<typeof prospectScopeSchema>;

/** Fields AI enrichment can fill on a prospect. */
export const enrichableFieldSchema = z.enum([
  "firstName",
  "lastName",
  "gender",
  "entityType",
  "country",
  "language",
  "b2bB2c",
  "niche",
  "accountAge",
  "avgLikes",
  "avgComments",
  "engagementRate",
  "websiteContacts",
]);
export type EnrichableField = z.infer<typeof enrichableFieldSchema>;
